import { useState, useEffect, useMemo, useRef } from "react";
import { useLoaderData } from "react-router-dom";
import {
  FaTimes,
  FaExclamationTriangle,
  FaSpinner,
  FaSearch,
} from "react-icons/fa";
import Notification from "./Notification";
import XeokitViewer from "./XeokitViewer";
import ImageCarousel from "./ImageCarousel";
import * as databaseService from "../services/databaseService";
import {
  uploadToR2,
  generateModelPaths,
  deleteModelFromR2,
  getPublicFileUrl,
} from "../utils/storageHelpers";
import "../styles/admin.css";

const CATEGORIES = ["Zelige", "Boiserie", "Platre", "Ferronnerie"];

// Lazy loading component for XeokitViewer with intersection observer
function LazyXeokitViewer({ modelUrl, height, width, enableZoom, modelId }) {
  const [shouldLoad, setShouldLoad] = useState(false);
  const containerRef = useRef(null);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    // Once loaded, stay loaded (pagination limits us to 12 items anyway)
    if (hasLoadedRef.current) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasLoadedRef.current) {
          // Add a small random delay to stagger loading
          const delay = Math.random() * 200;
          setTimeout(() => {
            setShouldLoad(true);
            hasLoadedRef.current = true;
          }, delay);
        }
      },
      {
        threshold: 0.1,
        rootMargin: '200px' // Start loading earlier
      }
    );

    const currentRef = containerRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []); // Only run once on mount

  if (!shouldLoad) {
    return (
      <div
        ref={containerRef}
        style={{
          width: width || '100%',
          height: height || '100%',
          background: 'var(--color-viewport-bg, #0A0A0A)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <p style={{ color: 'var(--color-on-surface-secondary, #666)' }}>
          Prévisualisation 3D
        </p>
      </div>
    );
  }

  return (
    <XeokitViewer
      modelUrl={modelUrl}
      height={height}
      width={width}
      enableZoom={enableZoom}
    />
  );
}

function AdminDashboard() {
  const { user, userRecord, models: initialModels } = useLoaderData();
  const [objectList, setObjectList] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [notification, setNotification] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: "",
    category: "Zelige",
    description: "",
    displayFile: null,
    displayFileName: "",
    displayFileType: "",
    fileSize: "",
    displaySize: 0,
  });

  // Individual file state for each download format
  const [selectedFiles, setSelectedFiles] = useState({
    dwg: null,
    ifc: null,
    rfa: null,
    rvt: null,
  });

  // Mockup images state (up to 6 images)
  const [mockupImages, setMockupImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState("Tous");
  const [filterFeatured, setFilterFeatured] = useState("Tous");
  const [inspectingModel, setInspectingModel] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  // Subscribe to real-time model updates
  useEffect(() => {
    setLoading(true);

    // Set up real-time listener
    const unsubscribe = databaseService.listenToModels((models) => {
      setObjectList(models);
      setLoading(false);
    });

    // Cleanup listener on unmount
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  // Filter and search models
  const filteredModels = useMemo(() => {
    return objectList.filter((model) => {
      // Search filter
      const matchesSearch =
        searchQuery === "" ||
        model.model_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        model.model_description
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase());

      // Category filter
      const matchesCategory =
        filterCategory === "Tous" || model.model_category === filterCategory;

      // Featured filter
      const matchesFeatured =
        filterFeatured === "Tous" ||
        (filterFeatured === "Vedette" && model.featured) ||
        (filterFeatured === "Standard" && !model.featured);

      return matchesSearch && matchesCategory && matchesFeatured;
    });
  }, [objectList, searchQuery, filterCategory, filterFeatured]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredModels.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedModels = filteredModels.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterCategory, filterFeatured]);

  const showNotification = (message, type = "success") => {
    setNotification({ message, type });
  };

  const handleEdit = (obj) => {
    setEditingId(obj.model_id);

    // Determine display file type from XKT URL
    const displayUrl = obj.model_xkt_url;
    let displayFileType = "xkt";
    if (displayUrl) {
      const ext = displayUrl.split(".").pop().toLowerCase();
      if (["xkt", "jpg", "jpeg", "png", "gif", "webp"].includes(ext)) {
        displayFileType = ext;
      }
    }

    setFormData({
      name: obj.model_name,
      category: obj.model_category,
      description: obj.model_description,
      displayFile: displayUrl,
      displayFileName: obj.filename,
      displayFileType: displayFileType,
      fileSize: obj.model_xkt_size
        ? `${(obj.model_xkt_size / (1024 * 1024)).toFixed(2)} Mo`
        : "",
      displaySize: obj.model_xkt_size,
    });

    // Load existing download files from model_files
    const modelFiles = obj.model_files || {};
    setSelectedFiles({
      dwg: modelFiles.dwg ? { url: modelFiles.dwg.url, size: modelFiles.dwg.size, existing: true, name: 'Fichier existant' } : null,
      ifc: modelFiles.ifc ? { url: modelFiles.ifc.url, size: modelFiles.ifc.size, existing: true, name: 'Fichier existant' } : null,
      rfa: modelFiles.rfa ? { url: modelFiles.rfa.url, size: modelFiles.rfa.size, existing: true, name: 'Fichier existant' } : null,
      rvt: modelFiles.rvt ? { url: modelFiles.rvt.url, size: modelFiles.rvt.size, existing: true, name: 'Fichier existant' } : null,
    });

    // Load existing mockup images
    const existingMockups = obj.mockup_images || [];
    setMockupImages(existingMockups.map((img, index) => ({
      url: img.url,
      size: img.size,
      existing: true,
      name: `Image ${index + 1}`
    })));

    setShowModal(true);
  };

  const handleInspect = (obj, e) => {
    e.stopPropagation();
    setInspectingModel(obj);
  };

  const closeInspectModal = () => {
    setInspectingModel(null);
  };

  const confirmDelete = (id) => {
    setDeleteId(id);
    setShowConfirmModal(true);
  };

  const handleDelete = async () => {
    try {
      // Find the model to get its name for R2 deletion
      const model = objectList.find((obj) => obj.model_id === deleteId);

      if (model) {
        // Delete from R2 storage first
        try {
          await deleteModelFromR2(model.model_name);
        } catch (r2Error) {
          console.warn("R2 deletion failed (files may not exist):", r2Error);
          // Continue with database deletion even if R2 fails
        }
      }

      // Delete from Firebase database
      await databaseService.deleteModel(deleteId);

      setShowConfirmModal(false);
      setDeleteId(null);
      showNotification("Objet supprimé avec succès", "success");
    } catch (error) {
      console.error("Error deleting model:", error);
      showNotification("Erreur lors de la suppression", "error");
    }
  };

  const handleDisplayFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Determine file type from extension
      const ext = file.name.split(".").pop().toLowerCase();
      const fileName = file.name.replace(/\.[^/.]+$/, "");
      const formattedName = fileName
        .replace(/[_-]/g, " ")
        .replace(/\b\w/g, (l) => l.toUpperCase());

      setFormData((prev) => ({
        ...prev,
        name: prev.name || formattedName,
        displayFile: file,
        displayFileName: file.name,
        displayFileType: ext,
        fileSize: (file.size / (1024 * 1024)).toFixed(2) + " Mo",
        displaySize: file.size,
      }));
    }
  };

  /**
   * Handle file change for a specific download format
   * @param {string} format - The file format (dwg, ifc, rfa, rvt)
   * @param {File|null} file - The selected file or null
   */
  const handleDownloadFileChange = (format, file) => {
    setSelectedFiles(prev => ({
      ...prev,
      [format]: file ? { file, name: file.name, size: file.size } : null
    }));
  };

  /**
   * Remove a specific download file from selection
   * @param {string} format - File format to remove
   */
  const removeDownloadFile = (format) => {
    setSelectedFiles(prev => ({
      ...prev,
      [format]: null
    }));
  };

  /**
   * Handle mockup images selection (up to 6 images)
   * @param {FileList} files - Selected files
   */
  const handleMockupImagesChange = (files) => {
    const newImages = Array.from(files).slice(0, 6 - mockupImages.length).map(file => ({
      file,
      name: file.name,
      size: file.size
    }));
    setMockupImages(prev => [...prev, ...newImages].slice(0, 6));
  };

  /**
   * Remove a specific mockup image
   * @param {number} index - Index of image to remove
   */
  const removeMockupImage = (index) => {
    setMockupImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if either XKT file or mockup images are provided for display
    const hasDisplayFile = formData.displayFile || mockupImages.length > 0;
    if (!hasDisplayFile) {
      showNotification(
        "Veuillez télécharger un fichier XKT ou des images de présentation",
        "error"
      );
      return;
    }

    // Check if at least one download file is selected
    const hasDownloadFile = selectedFiles.dwg || selectedFiles.ifc || selectedFiles.rfa || selectedFiles.rvt;
    if (!hasDownloadFile) {
      showNotification(
        "Veuillez télécharger au moins un fichier (DWG, IFC, RVT ou RFA)",
        "error"
      );
      return;
    }

    if (!user || !user.uid) {
      showNotification("Utilisateur non authentifié", "error");
      return;
    }

    setUploading(true);
    setUploadProgress("Préparation...");

    try {
      // Generate organized paths
      const paths = generateModelPaths(formData.name);

      let displayUrl = null;
      const modelFiles = {};

      // Upload display file (XKT or image) to R2
      if (formData.displayFile instanceof File) {
        const fileType = formData.displayFileType;
        const uploadType = ["jpg", "jpeg", "png", "gif", "webp"].includes(
          fileType
        )
          ? "image"
          : "xkt";
        setUploadProgress(
          `Upload du fichier d'affichage ${fileType.toUpperCase()}...`
        );
        const displayResult = await uploadToR2(
          formData.displayFile,
          formData.name,
          uploadType
        );
        displayUrl = displayResult.path;
      } else if (typeof formData.displayFile === 'string') {
        // Keep existing URL
        displayUrl = formData.displayFile;
      }

      // Upload DWG file if provided
      if (selectedFiles.dwg) {
        if (selectedFiles.dwg.file) {
          setUploadProgress("Upload du fichier DWG...");
          const dwgResult = await uploadToR2(selectedFiles.dwg.file, formData.name, "dwg");
          modelFiles.dwg = { url: dwgResult.path, size: dwgResult.size };
        } else if (selectedFiles.dwg.existing) {
          modelFiles.dwg = { url: selectedFiles.dwg.url, size: selectedFiles.dwg.size };
        }
      }

      // Upload IFC file if provided
      if (selectedFiles.ifc) {
        if (selectedFiles.ifc.file) {
          setUploadProgress("Upload du fichier IFC...");
          const ifcResult = await uploadToR2(selectedFiles.ifc.file, formData.name, "ifc");
          modelFiles.ifc = { url: ifcResult.path, size: ifcResult.size };
        } else if (selectedFiles.ifc.existing) {
          modelFiles.ifc = { url: selectedFiles.ifc.url, size: selectedFiles.ifc.size };
        }
      }

      // Upload RFA file if provided
      if (selectedFiles.rfa) {
        if (selectedFiles.rfa.file) {
          setUploadProgress("Upload du fichier RFA...");
          const rfaResult = await uploadToR2(selectedFiles.rfa.file, formData.name, "rfa");
          modelFiles.rfa = { url: rfaResult.path, size: rfaResult.size };
        } else if (selectedFiles.rfa.existing) {
          modelFiles.rfa = { url: selectedFiles.rfa.url, size: selectedFiles.rfa.size };
        }
      }

      // Upload RVT file if provided
      if (selectedFiles.rvt) {
        if (selectedFiles.rvt.file) {
          setUploadProgress("Upload du fichier RVT...");
          const rvtResult = await uploadToR2(selectedFiles.rvt.file, formData.name, "rvt");
          modelFiles.rvt = { url: rvtResult.path, size: rvtResult.size };
        } else if (selectedFiles.rvt.existing) {
          modelFiles.rvt = { url: selectedFiles.rvt.url, size: selectedFiles.rvt.size };
        }
      }

      // Upload mockup images if provided
      const uploadedMockups = [];
      for (let i = 0; i < mockupImages.length; i++) {
        const img = mockupImages[i];
        if (img.file) {
          setUploadProgress(`Upload de l'image ${i + 1}/${mockupImages.length}...`);
          const imgResult = await uploadToR2(img.file, formData.name, `mockup-${i + 1}`);
          uploadedMockups.push({ url: imgResult.path, size: imgResult.size });
        } else if (img.existing) {
          uploadedMockups.push({ url: img.url, size: img.size });
        }
      }

      // Save to Firebase
      setUploadProgress("Enregistrement dans la base de données...");
      const modelData = {
        filename: formData.displayFileName || formData.name,
        model_name: formData.name,
        model_folder: paths.folder,
        model_category: formData.category,
        model_description: formData.description,
        model_owner: user.uid,
        model_files: modelFiles,
        model_xkt_url: displayUrl,
        model_display_type: formData.displayFileType,
        model_xkt_size: formData.displaySize || 0,
        mockup_images: uploadedMockups,
        // Keep legacy fields for backward compatibility
        model_ifc_url: modelFiles.ifc?.url || null,
        model_ifc_size: modelFiles.ifc?.size || null,
      };

      if (editingId) {
        // Update existing model
        await databaseService.updateModel(editingId, modelData);
        showNotification("Objet mis à jour avec succès", "success");
      } else {
        // Create new model
        await databaseService.createModel(modelData);
        showNotification("Objet créé avec succès", "success");
      }

      closeModal();
    } catch (error) {
      console.error("Error saving model:", error);
      showNotification(`Erreur: ${error.message}`, "error");
    } finally {
      setUploading(false);
      setUploadProgress("");
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setFormData({
      name: "",
      category: "Zelige",
      description: "",
      displayFile: null,
      displayFileName: "",
      displayFileType: "",
      fileSize: "",
      displaySize: 0,
    });
    setSelectedFiles({
      dwg: null,
      ifc: null,
      rfa: null,
      rvt: null,
    });
    setMockupImages([]);
  };

  const toggleFeatured = async (id) => {
    try {
      const model = objectList.find((obj) => obj.model_id === id);
      if (model) {
        await databaseService.updateModel(id, {
          featured: !model.featured,
        });
      }
    } catch (error) {
      console.error("Error toggling featured:", error);
      showNotification("Erreur lors de la mise à jour", "error");
    }
  };

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <div>
          <h1>Gestionnaire de Modèles BIM</h1>
          <p className="admin-subtitle">
            Gérez l'inventaire de modèles IFC/XKT
          </p>
        </div>
        <div style={{ display: "flex", gap: "10px" }}>
          <button className="btn-add-object" onClick={() => setShowModal(true)}>
            Ajouter un Modèle
          </button>
        </div>
      </div>

      <div className="admin-filters">
        <div className="search-box">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Rechercher par nom ou description..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          {searchQuery && (
            <button
              className="search-clear"
              onClick={() => setSearchQuery("")}
              title="Effacer"
            >
              <FaTimes />
            </button>
          )}
        </div>
        <div className="filter-group">
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="filter-select"
          >
            <option value="Tous">Toutes catégories</option>
            {CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
          <select
            value={filterFeatured}
            onChange={(e) => setFilterFeatured(e.target.value)}
            className="filter-select"
          >
            <option value="Tous">Tous les modèles</option>
            <option value="Vedette">Vedettes uniquement</option>
            <option value="Standard">Standards uniquement</option>
          </select>
        </div>
        <div className="filter-results">
          {filteredModels.length} modèle{filteredModels.length !== 1 ? "s" : ""}{" "}
          trouvé{filteredModels.length !== 1 ? "s" : ""}
        </div>
      </div>

      {loading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            color: "var(--color-on-surface-secondary, #666)",
            zIndex: 1099,
            backgroundColor: "hsla(30, 20%, 6%, 0.5)",
            backdropFilter: "blur(2px)",
            pointerEvents: showModal || inspectingModel ? "none" : "auto",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "1rem" }}>
            <FaSpinner className="spin" style={{ fontSize: "2rem" }} />
            <p>Chargement des modèles...</p>
          </div>
        </div>
      )}

      {!loading && objectList.length === 0 && (
        <div
          style={{
            textAlign: "center",
            padding: "40px",
            color: "var(--color-on-surface-secondary, #666)",
          }}
        >
          <p>Aucun modèle trouvé dans la base de données.</p>
          <p style={{ fontSize: "14px", marginTop: "10px" }}>
            Cliquez sur "Ajouter un Modèle" pour commencer.
          </p>
        </div>
      )}

      {!loading && objectList.length > 0 && filteredModels.length === 0 && (
        <div
          style={{
            textAlign: "center",
            padding: "40px",
            color: "var(--muted-foreground)",
          }}
        >
          <p>Aucun modèle ne correspond à vos critères de recherche.</p>
          <button
            onClick={() => {
              setSearchQuery("");
              setFilterCategory("Tous");
              setFilterFeatured("Tous");
            }}
            style={{
              marginTop: "1rem",
              background: "transparent",
              border: "1px solid var(--accent)",
              color: "var(--accent)",
              padding: "0.5rem 1rem",
              borderRadius: "6px",
              cursor: "pointer",
            }}
          >
            Réinitialiser les filtres
          </button>
        </div>
      )}

      <div className="objects-grid">
        {paginatedModels.map((obj, index) => (
          <div
            key={obj.model_id}
            className={`object-card animate-fade-in-up ${
              obj.featured ? "featured" : ""
            }`}
            style={{ "--index": index, cursor: "pointer" }}
            onClick={() => handleInspect(obj, event)}
          >
            <div className="object-card-preview">
              {obj.model_xkt_url ? (
                (() => {
                  const fileUrl = getPublicFileUrl(obj.model_xkt_url);
                  const fileExtension = fileUrl.split(".").pop().toLowerCase();
                  const isImage = [
                    "jpg",
                    "jpeg",
                    "png",
                    "gif",
                    "webp",
                  ].includes(fileExtension);

                  return isImage ? (
                    <img
                      src={fileUrl}
                      alt={obj.model_name}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        borderRadius: "8px 8px 0 0",
                      }}
                      onError={(e) => {
                        e.target.style.display = "none";
                        e.target.nextSibling.style.display = "flex";
                      }}
                    />
                  ) : (
                    <LazyXeokitViewer
                      modelUrl={fileUrl}
                      height="100%"
                      width="100%"
                      modelId={obj.model_id}
                    />
                  );
                })()
              ) : obj.mockup_images && obj.mockup_images.length > 0 ? (
                <img
                  src={getPublicFileUrl(obj.mockup_images[0].url)}
                  alt={obj.model_name}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    borderRadius: "8px 8px 0 0",
                  }}
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.nextSibling.style.display = "flex";
                  }}
                />
              ) : (
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    background: "var(--color-viewport-bg, #0A0A0A)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <p
                    style={{ color: "var(--color-on-surface-secondary, #666)" }}
                  >
                    Pas de prévisualisation
                  </p>
                </div>
              )}
              {/* Fallback error display */}
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  background: "var(--color-viewport-bg, #0A0A0A)",
                  display: "none",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <p style={{ color: "var(--color-on-surface-secondary, #666)" }}>
                  Erreur de chargement
                </p>
              </div>
              {obj.featured && <span className="featured-badge">Vedette</span>}
            </div>
            <div className="object-card-content">
              <div className="card-header">
                <h3>{obj.model_name}</h3>
                <span className="object-category">{obj.model_category}</span>
              </div>
              <div className="object-meta">
                <span>
                  Taille:{" "}
                  {obj.model_xkt_size
                    ? `${(obj.model_xkt_size / (1024 * 1024)).toFixed(2)} Mo`
                    : "N/A"}
                </span>
                <span>Téléchargements: {obj.downloads || 0}</span>
              </div>
            </div>
            <div className="object-card-actions">
              <label className="featured-toggle">
                <input
                  type="checkbox"
                  checked={obj.featured || false}
                  onChange={(e) => {
                    e.stopPropagation();
                    toggleFeatured(obj.model_id);
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
                Vedette
              </label>
              <div className="action-buttons">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEdit(obj);
                  }}
                  className="btn-edit"
                  title="Modifier"
                >
                  Modifier
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    confirmDelete(obj.model_id);
                  }}
                  className="btn-delete"
                  title="Supprimer"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            ← Précédent
          </button>
          <div className="pagination-info">
            Page {currentPage} sur {totalPages} ({filteredModels.length} modèles)
          </div>
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          >
            Suivant →
          </button>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {editingId ? "Modifier le Modèle" : "Ajouter un Nouveau Modèle"}
              </h2>
              <button className="modal-close" onClick={closeModal}>
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="modal-form">
              {/* Display Options Section */}
              <div className="form-group">
                <label style={{ fontSize: "1rem", marginBottom: "0.75rem" }}>
                  Visualisation du modèle * (choisir une option)
                </label>
                <p style={{ fontSize: "12px", color: "var(--color-on-surface-secondary, #888)", marginBottom: "16px" }}>
                  Téléchargez un fichier XKT pour la visualisation 3D, ou des images de présentation. Si les deux sont fournis, le fichier XKT sera utilisé.
                </p>
              </div>

              {/* Option 1: XKT File */}
              <div className="form-group" style={{ 
                padding: "1rem", 
                background: formData.displayFile ? "rgba(0, 168, 150, 0.1)" : "var(--muted)", 
                border: formData.displayFile ? "2px solid var(--accent)" : "1px solid var(--border)",
                borderRadius: "8px",
                marginBottom: "0.75rem"
              }}>
                <label style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <span style={{ 
                    background: "var(--accent)", 
                    color: "var(--accent-foreground)", 
                    padding: "0.125rem 0.5rem", 
                    borderRadius: "4px", 
                    fontSize: "0.7rem", 
                    fontWeight: "600" 
                  }}>OPTION 1</span>
                  Fichier XKT (visualisation 3D interactive)
                </label>
                <input
                  type="file"
                  accept=".xkt"
                  onChange={handleDisplayFileUpload}
                  className="file-input"
                  disabled={uploading}
                  style={{ marginTop: "0.5rem" }}
                />
                {formData.displayFileName && (
                  <div className="file-preview">
                    {formData.displayFileName} ({formData.fileSize})
                    <span className="file-type-badge">XKT</span>
                  </div>
                )}
              </div>

              {/* Option 2: Mockup Images */}
              <div className="form-group" style={{ 
                padding: "1rem", 
                background: (!formData.displayFile && mockupImages.length > 0) ? "rgba(0, 168, 150, 0.1)" : "var(--muted)", 
                border: (!formData.displayFile && mockupImages.length > 0) ? "2px solid var(--accent)" : "1px solid var(--border)",
                borderRadius: "8px"
              }}>
                <label style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <span style={{ 
                    background: "var(--accent)", 
                    color: "var(--accent-foreground)", 
                    padding: "0.125rem 0.5rem", 
                    borderRadius: "4px", 
                    fontSize: "0.7rem", 
                    fontWeight: "600" 
                  }}>OPTION 2</span>
                  Images de présentation (max 6)
                </label>
                <p style={{ fontSize: "11px", color: "var(--color-on-surface-secondary, #888)", margin: "0.5rem 0" }}>
                  Les images seront affichées en carrousel si aucun fichier XKT n'est fourni
                </p>
                <input
                  type="file"
                  accept=".jpg,.jpeg,.png,.gif,.webp"
                  multiple
                  onChange={(e) => handleMockupImagesChange(e.target.files)}
                  className="file-input"
                  disabled={uploading || mockupImages.length >= 6}
                />
                {mockupImages.length > 0 && (
                  <div className="mockup-images-list">
                    {mockupImages.map((img, index) => (
                      <div key={index} className="file-preview">
                        {img.name} ({(img.size / 1024 / 1024).toFixed(2)} Mo)
                        <span className="file-type-badge">IMAGE</span>
                        <button
                          type="button"
                          onClick={() => removeMockupImage(index)}
                          disabled={uploading}
                          className="file-remove-btn"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {mockupImages.length > 0 && (
                  <p style={{ fontSize: "12px", color: "var(--color-on-surface-secondary, #888)", marginTop: "8px" }}>
                    {mockupImages.length}/6 images sélectionnées
                  </p>
                )}
              </div>

              <div className="form-group" style={{ marginTop: "1.5rem" }}>
                <label>Fichiers à télécharger * (au moins un requis)</label>
                <p style={{ fontSize: "12px", color: "var(--color-on-surface-secondary, #888)", marginBottom: "12px" }}>
                  Sélectionnez les formats disponibles pour ce modèle (DWG, IFC, RFA ou RVT)
                </p>
              </div>

              {/* DWG Input */}
              <div className="form-group">
                <label>Fichier DWG</label>
                <input
                  type="file"
                  accept=".dwg"
                  onChange={(e) => handleDownloadFileChange('dwg', e.target.files[0] || null)}
                  className="file-input"
                  disabled={uploading}
                />
                {selectedFiles.dwg && (
                  <div className="file-preview">
                    {selectedFiles.dwg.name} ({(selectedFiles.dwg.size / 1024 / 1024).toFixed(2)} Mo)
                    <span className="file-type-badge">DWG</span>
                    <button
                      type="button"
                      onClick={() => removeDownloadFile('dwg')}
                      disabled={uploading}
                      className="file-remove-btn"
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>

              {/* IFC Input */}
              <div className="form-group">
                <label>Fichier IFC</label>
                <input
                  type="file"
                  accept=".ifc"
                  onChange={(e) => handleDownloadFileChange('ifc', e.target.files[0] || null)}
                  className="file-input"
                  disabled={uploading}
                />
                {selectedFiles.ifc && (
                  <div className="file-preview">
                    {selectedFiles.ifc.name} ({(selectedFiles.ifc.size / 1024 / 1024).toFixed(2)} Mo)
                    <span className="file-type-badge">IFC</span>
                    <button
                      type="button"
                      onClick={() => removeDownloadFile('ifc')}
                      disabled={uploading}
                      className="file-remove-btn"
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>

              {/* RFA Input */}
              <div className="form-group">
                <label>Fichier RFA (Famille Revit)</label>
                <input
                  type="file"
                  accept=".rfa"
                  onChange={(e) => handleDownloadFileChange('rfa', e.target.files[0] || null)}
                  className="file-input"
                  disabled={uploading}
                />
                {selectedFiles.rfa && (
                  <div className="file-preview">
                    {selectedFiles.rfa.name} ({(selectedFiles.rfa.size / 1024 / 1024).toFixed(2)} Mo)
                    <span className="file-type-badge">RFA</span>
                    <button
                      type="button"
                      onClick={() => removeDownloadFile('rfa')}
                      disabled={uploading}
                      className="file-remove-btn"
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>

              {/* RVT Input */}
              <div className="form-group">
                <label>Fichier RVT (Revit)</label>
                <input
                  type="file"
                  accept=".rvt"
                  onChange={(e) => handleDownloadFileChange('rvt', e.target.files[0] || null)}
                  className="file-input"
                  disabled={uploading}
                />
                {selectedFiles.rvt && (
                  <div className="file-preview">
                    {selectedFiles.rvt.name} ({(selectedFiles.rvt.size / 1024 / 1024).toFixed(2)} Mo)
                    <span className="file-type-badge">RVT</span>
                    <button
                      type="button"
                      onClick={() => removeDownloadFile('rvt')}
                      disabled={uploading}
                      className="file-remove-btn"
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Nom du Modèle *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="Rempli automatiquement depuis le nom du fichier"
                  required
                  disabled={uploading}
                />
              </div>

              <div className="form-group">
                <label>Catégorie *</label>
                <select
                  value={formData.category}
                  onChange={(e) =>
                    setFormData({ ...formData, category: e.target.value })
                  }
                  disabled={uploading}
                >
                  {CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Description détaillée..."
                  rows="4"
                  required
                  disabled={uploading}
                />
              </div>

              {uploadProgress && (
                <div
                  style={{
                    padding: "1rem",
                    background: "rgba(0, 168, 150, 0.1)",
                    border: "1px solid rgba(0, 168, 150, 0.3)",
                    borderRadius: "8px",
                    color: "#00A896",
                    textAlign: "center",
                  }}
                >
                  <FaSpinner className="spin" /> {uploadProgress}
                </div>
              )}

              <div className="modal-actions">
                <button
                  type="button"
                  onClick={closeModal}
                  className="btn-cancel"
                  disabled={uploading}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="btn-submit"
                  disabled={uploading}
                >
                  {uploading
                    ? "Upload en cours..."
                    : editingId
                    ? "Mettre à Jour"
                    : "Créer"}{" "}
                  {!uploading && "le Modèle"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showConfirmModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowConfirmModal(false)}
        >
          <div
            className="modal-content modal-small"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>
                <FaExclamationTriangle style={{ marginRight: "8px" }} />{" "}
                Confirmer la Suppression
              </h2>
              <button
                className="modal-close"
                onClick={() => setShowConfirmModal(false)}
              >
                <FaTimes />
              </button>
            </div>
            <div className="modal-body">
              <p>
                Êtes-vous sûr de vouloir supprimer ce modèle ? Cette action ne
                peut pas être annulée.
              </p>
            </div>
            <div className="modal-actions">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="btn-cancel"
              >
                Annuler
              </button>
              <button onClick={handleDelete} className="btn-delete">
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      {/* Inspection Modal */}
      {inspectingModel && (
        <div className="modal-overlay" onClick={closeInspectModal}>
          <div
            className="modal-content modal-inspect"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <div>
                <h2>{inspectingModel.model_name}</h2>
                <span className="object-category" style={{ marginLeft: "1rem" }}>
                  {inspectingModel.model_category}
                </span>
              </div>
              <button className="modal-close" onClick={closeInspectModal}>
                <FaTimes />
              </button>
            </div>

            <div className="modal-body" style={{ padding: 0 }}>
              <div className="inspect-viewer">
                {inspectingModel.model_xkt_url ? (
                  (() => {
                    const fileUrl = getPublicFileUrl(inspectingModel.model_xkt_url);
                    const fileExtension = fileUrl.split(".").pop().toLowerCase();
                    const isImage = ["jpg", "jpeg", "png", "gif", "webp"].includes(fileExtension);

                    return isImage ? (
                      <img
                        src={fileUrl}
                        alt={inspectingModel.model_name}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          background: "var(--muted)",
                        }}
                      />
                    ) : (
                      <LazyXeokitViewer
                        modelUrl={fileUrl}
                        height="600px"
                        width="100%"
                        enableZoom={true}
                        modelId={inspectingModel.model_id}
                      />
                    );
                  })()
                ) : inspectingModel.mockup_images && inspectingModel.mockup_images.length > 0 ? (
                  <ImageCarousel
                    images={inspectingModel.mockup_images.map(img => ({
                      url: getPublicFileUrl(img.url),
                      size: img.size
                    }))}
                    alt={inspectingModel.model_name}
                    height="600px"
                  />
                ) : (
                  <div
                    style={{
                      width: "100%",
                      height: "600px",
                      background: "var(--muted)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <p style={{ color: "var(--muted-foreground)" }}>
                      Pas de prévisualisation disponible
                    </p>
                  </div>
                )}
              </div>

              <div className="inspect-details">
                <div className="detail-section">
                  <h3>Description</h3>
                  <p>{inspectingModel.model_description || "Aucune description"}</p>
                </div>

                <div className="detail-section">
                  <h3>Informations</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">Taille:</span>
                      <span className="detail-value">
                        {inspectingModel.model_xkt_size
                          ? `${(inspectingModel.model_xkt_size / (1024 * 1024)).toFixed(2)} Mo`
                          : "N/A"}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Téléchargements:</span>
                      <span className="detail-value">{inspectingModel.downloads || 0}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Catégorie:</span>
                      <span className="detail-value">{inspectingModel.model_category}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Statut:</span>
                      <span className="detail-value">
                        {inspectingModel.featured ? "⭐ Vedette" : "Standard"}
                      </span>
                    </div>
                  </div>
                </div>

                {inspectingModel.model_files && Object.keys(inspectingModel.model_files).length > 0 && (
                  <div className="detail-section">
                    <h3>Fichiers disponibles</h3>
                    <div className="available-files">
                      {Object.entries(inspectingModel.model_files).map(([format, file]) => (
                        <div key={format} className="file-badge">
                          <span className="file-format">{format.toUpperCase()}</span>
                          <span className="file-size">
                            {(file.size / (1024 * 1024)).toFixed(2)} Mo
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {inspectingModel.mockup_images && inspectingModel.mockup_images.length > 0 && (
                  <div className="detail-section">
                    <h3>Images de présentation</h3>
                    <p style={{ fontSize: "0.9rem", color: "var(--muted-foreground)" }}>
                      {inspectingModel.mockup_images.length} image{inspectingModel.mockup_images.length > 1 ? "s" : ""} disponible{inspectingModel.mockup_images.length > 1 ? "s" : ""}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-actions">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  closeInspectModal();
                  handleEdit(inspectingModel);
                }}
                className="btn-edit"
              >
                Modifier
              </button>
              <button onClick={closeInspectModal} className="btn-cancel">
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;
